import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { api, isAbort, useApi } from "@/lib/api";
import { toStory } from "@/lib/mappers";
import { SubscribeButton } from "@/components/site/SubscribeButton";
import { PageHeader, EmptyState, LoadingState, ErrorState } from "@/components/site/Content";
import { StoryRow } from "@/components/site/StoryCard";

import { useCopy } from "@/lib/copy";
import { Rich } from "@/components/site/Rich";

const PAGE = 20;

export default function Research() {
  usePageMeta("Research in Namma Ooru: Bengaluru Research & Innovation Stories", "Stories of Bengaluru research and innovation: new technologies, labs, institutions, researchers and startups from across the city.");
  const t = useCopy();
  const categories = useApi("/story-categories").data || [];
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("All");
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: false, more: false });

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const params = (skip) => ({ q: debounced || undefined, category: category === "All" ? undefined : category, limit: PAGE, skip });

  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: false }));
    api
      .get("/stories", { params: params(0), signal: ctrl.signal })
      .then(({ data }) => setState({ items: data.items.map(toStory), total: data.total, loading: false, error: false, more: false }))
      .catch((e) => !isAbort(e) && setState((s) => ({ ...s, loading: false, error: true })));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, category]);

  const loadMore = () => {
    setState((s) => ({ ...s, more: true }));
    api
      .get("/stories", { params: params(state.items.length) })
      .then(({ data }) => setState((s) => ({ ...s, items: [...s.items, ...data.items.map(toStory)], total: data.total, more: false })))
      .catch(() => setState((s) => ({ ...s, more: false, error: true })));
  };

  const reset = () => { setQuery(""); setCategory("All"); };
  const results = state.items;

  return (
    <>
      <PageHeader eyebrow={t("stories.header.eyebrow")} title={t("stories.header.title")} intro={t("stories.header.intro") && <Rich text={t("stories.header.intro")} />}>
        <SubscribeButton label={t("stories.header.subscribe")} href={t("contact.social.prasaraUrl")} note={t("stories.header.nudge")} testId="stories-subscribe-button" />
      </PageHeader>

      <section className="container-x py-10 md:py-14" aria-label="Story list">
        <div className="flex flex-col gap-6 border-b border-line pb-8">
          <div className="relative max-w-xl">
            <label htmlFor="story-search" className="sr-only">Search stories</label>
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-muted" aria-hidden="true" />
            <input id="story-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("stories.list.searchPlaceholder")} className="field pl-12" data-testid="story-search-input" />
          </div>
          <div role="group" aria-label="Filter by category" className="flex flex-wrap gap-2">
            {["All", ...categories].map((c) => (
              <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className="pill" data-testid={`story-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {c}
              </button>
            ))}
          </div>
          <p className="text-sm text-ink-muted" aria-live="polite" data-testid="story-results-count">
            {state.loading ? "Loading stories…" : `${state.total} ${state.total === 1 ? "story" : "stories"}`}
          </p>
        </div>

        {state.loading ? (
          <LoadingState className="mt-8 h-96" label="Loading stories" />
        ) : state.error && !results.length ? (
          <div className="mt-10"><ErrorState testId="story-error-state" /></div>
        ) : results.length ? (
          <>
            <div className="divide-y divide-line">{results.map((s) => <StoryRow key={s.slug} story={s} />)}</div>
            {results.length < state.total && (
              <div className="mt-8 border-t border-line pt-8">
                <button type="button" onClick={loadMore} disabled={state.more} className="pill disabled:opacity-60" data-testid="story-load-more">
                  {state.more ? "Loading…" : "Load more stories"}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-10">
            <EmptyState
              title={t("stories.list.emptyTitle")}
              message={t("stories.list.emptyText")}
              testId="story-empty-state"
              action={<button type="button" onClick={reset} className="pill" data-testid="story-reset-filters">Clear search and filters</button>}
            />
          </div>
        )}
      </section>
    </>
  );
}
